import FreeQuizSessionDialog from "@features/auth/components/dialogs/FreeQuizSessionDialog";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { formatDate } from "@memorang/helpers/date";
import Box from "@memorang/ui/components/Box";
import ProductIcon from "@memorang/ui/components/ProductIcon";
import { useDevice } from "@memorang/ui/useDevice";
import { fetchExamDetails } from "api/commerce/exam-service";
import type { AccessCodeData } from "api/commerce/types";
import { useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, List, Text } from "react-native-paper";
import { events, useRedeemCopilotAnalytics } from "../analytics";
import { formatCase } from "../helpers";

export type AccessContentProps = {
	success: boolean;
	data?: AccessCodeData;
	productData?: AccessCodeData[];
};

export const AccessContent = ({
	success,
	data,
	productData,
}: AccessContentProps) => {
	const router = useRouter();
	const { isMobile } = useDevice();
	const { t, i18n } = useTranslation();

	const { trackRedeemCopilotEvent } = useRedeemCopilotAnalytics();

	const [showFreeQuizDialog, setShowFreeQuizDialog] = useState(false);

	const { handleSwitchExamContext } = useExamContext();

	const items = (success ? productData || [] : [data]).filter(
		(item) => item != null,
	);

	if (!items || items.length === 0) {
		return;
	}

	const handleAccessContent = async (item: AccessCodeData) => {
		trackRedeemCopilotEvent(events.accessContentInitiated, {
			type: "success",
			message: "Access content initiated",
			switchToExamId: item.examId,
			switchToExamName: item.examName,
		});

		try {
			const examDetails = await fetchExamDetails({
				examId: item.examId,
			});

			await handleSwitchExamContext(examDetails);
			const hasPracticeTests = item.bundleType === "PRACTICE_TEST";
			const url =
				!hasPracticeTests || isMobile ? "/study-pack" : "/practice-tests";
			router.push(url);
			setShowFreeQuizDialog(true);

			trackRedeemCopilotEvent(events.examContextSwitchSucceeded, {
				type: "success",
				message: "Exam context switched successfully",
				switchToExamId: item.examId,
				switchToExamName: item.examName,
			});
		} catch (e) {
			trackRedeemCopilotEvent(events.examContextSwitchFailed, {
				type: "error",
				message: e instanceof Error ? e.message : "Exam context switch failed",
				switchToExamId: item.examId,
				switchToExamName: item.examName,
			});
		}
	};

	return (
		<Card mode="elevated" style={{ overflow: "hidden" }}>
			<Card.Title
				title={
					success
						? t("redeemCopilot.accessContent.redeemedTitle")
						: t("redeemCopilot.accessContent.previouslyRedeemedTitle")
				}
				titleVariant="titleLarge"
				subtitle={t("redeemCopilot.accessContent.subtitle")}
			/>
			{items.map((item) => {
				return (
					<List.Item
						key={item.bundleId}
						title={<Text variant="titleMedium">{item.examName}</Text>}
						description={
							<Box>
								<Text variant="labelSmall">
									{formatCase(item.bundleType)}
									{item.planLength && ` (${item.planLength})`}
								</Text>
								{item.expiryDate && (
									<Text variant="labelSmall">
										{t("redeemCopilot.accessContent.expires", {
											date: formatDate(item.expiryDate, i18n.language),
										})}
									</Text>
								)}
							</Box>
						}
						titleNumberOfLines={2}
						onPress={() => handleAccessContent(item)}
						left={(props) => (
							<List.Icon
								icon={() => <ProductIcon image={item.icon} size={48} />}
								{...props}
							/>
						)}
						right={(props) => (
							<List.Icon
								icon={() => (
									<Button
										mode="outlined"
										onPress={() => handleAccessContent(item)}
									>
										<Text variant="labelSmall">
											{t("redeemCopilot.accessContent.openCta")}
										</Text>
									</Button>
								)}
								{...props}
							/>
						)}
					/>
				);
			})}

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => setShowFreeQuizDialog(false)}
				/>
			)}
		</Card>
	);
};
